/**
 * How the sequence canvas NUMBERS its columns, and how long the cycle it draws is in days.
 *
 * The 07.09 mock (`Builder planu sekwencja.dc.html`) numbers straight through the gaps: `DZIEŃ 1`
 * over the first training, `DZIEŃ 2` over the one-day break after it, `DZIEŃ 3` over the next
 * training, `DNI 4–5` over a two-day break. Its own row summary then prints the total — "3 dni
 * treningowe · cykl 4 dni" — and the volume rail divides by the same figure to normalise a cycle's
 * sets to seven days.
 *
 * ── Why this module exists at all: the sum has exactly one home ──────────────────────────────
 * `Σ (1 + restDaysAfter)` lived in `volumeStatus` under a paragraph explaining why it was allowed,
 * and the mock now needs the same number in two more places — the column heads and the pattern row.
 * Three copies of one arithmetic is three answers to one question, and the mock itself computes
 * `cycLen` once and prints it eighty lines apart. So it is computed here, once, and interpolated.
 *
 * ── R2 as amended 2026-09-13 ────────────────────────────────────────────────────────────────
 * `self-paced-plan-requirements.md` R2 forbids summing a day's `restDaysAfter` into a cycle length
 * and forbids deriving any occurrence date from it, and R1 says a self-paced cycle has no length in
 * days. The amendment carves out exactly what this module does and nothing else: the sum may be
 * DRAWN as a label in the trainer's builder, where it binds nothing. It does not reach the wire, it
 * dates no occurrence, it enters no adherence figure and no forecast, and `TapeProjection` still
 * carries no `cycleLengthDays` field — putting it back there would hand the number to every consumer
 * of the projection, which is the door R2 is holding shut.
 *
 * ── Two different totals, deliberately, and what each one answers ────────────────────────────
 * `slots` numbers every column the canvas DRAWS. `cycleLengthDays` counts only the days the save
 * keeps. They diverge on exactly one tree: a day the trainer has just created and not yet filled,
 * which `draftToRequest`'s `phaseToInput` drops (`countsToward === false`). That day still has a
 * column, because the trainer just made it and the column is where they fill it, and its column
 * still has a number, because a canvas that skipped a number would read as a rendering fault. What
 * it must not do is enter the length the rail divides by — the client will never train it, so a
 * conversion using it averages a week's volume down over days that do not exist. The tile says so on
 * its own face (`DayCardView.unsavedNote`), which is where the two figures are reconciled for the
 * trainer.
 *
 * No React, no DOM, no query hook, no i18next: this is `model/`. It returns numbers; the words are
 * `tapeViewModel`'s.
 */

import type { TapeDay } from '~/entities/plan-draft';

/** One column of the canvas, numbered. A training, or the break the trainer put after it. */
export type CycleSlot =
  | {
      readonly kind: 'day';
      /** The day of the cycle this training falls on — the head's "DZIEŃ 3". */
      readonly dayNumber: number;
    }
  | {
      readonly kind: 'rest';
      /** The first day of the cycle the break covers. Meaningless when `days` is 0. */
      readonly from: number;
      /** The last day it covers. Equal to `from` on a one-day break. */
      readonly to: number;
      /** How many days it covers — `0` when the trainer prescribed no break here. */
      readonly days: number;
    };

export interface CycleNumbering {
  /**
   * `Σ (1 + restDaysAfter)` over the days the save KEEPS — see the header on why the two totals are
   * not the same number, and on why drawing this one is inside R2 as amended.
   */
  readonly cycleLengthDays: number;
  /** One entry per column the canvas draws, in the order it draws them: day, gap, day, gap… */
  readonly slots: readonly CycleSlot[];
}

/**
 * Number the cycle's columns and measure its length.
 *
 * Every training is followed by a gap slot, including the last one and including a gap of zero: the
 * canvas draws a column for each so the gap stays reachable, and `tapeViewModel` documents why.
 *
 * @param days The phase's days, in `Order`, as `tapeProjection` produced them.
 */
export function cycleDayNumbering(days: readonly TapeDay[]): CycleNumbering {
  const slots: CycleSlot[] = [];
  let dayNumber = 1;
  let cycleLengthDays = 0;

  for (const day of days) {
    slots.push({ kind: 'day', dayNumber });
    dayNumber += 1;

    const rest = day.restDaysAfter;
    // A gap of zero consumes no number and gets no head — `from`/`to` are still written out rather
    // than made optional, because a reader of the slot should not have to know that one field is
    // meaningless under another field's value. `days === 0` is the whole of the condition.
    slots.push({ kind: 'rest', from: dayNumber, to: dayNumber + Math.max(0, rest - 1), days: rest });
    dayNumber += rest;

    if (day.countsToward) cycleLengthDays += 1 + rest;
  }

  return { cycleLengthDays, slots };
}
